// src/api/productoRecepcion.js - VERSIÓN COMPLETA PARA LA NUEVA API
// API Base: /api/ProductoRecepcion

import { apiFetch, getCurrentApiUrl, getCurrentServer } from './index.js';

// ==================== CONSTANTES ====================
const API_BASE = '/api/ProductoRecepcion';

// ==================== FUNCIONES PRINCIPALES ====================

/**
 * Obtener todos los productos de recepción
 * @param {string} estado - 'todos' (default), 'disponible', 'vendido', 'reservado', etc.
 * @returns {Promise} Lista de productos de recepción
 */
export const obtenerProductosRecepcion = async (estado = 'todos') => {
  try {
    console.log('📋 Obteniendo productos de recepción...', { estado });
    
    let endpoint = API_BASE;
    
    if (estado !== 'todos' && estado) {
      endpoint = `${API_BASE}/estado/${encodeURIComponent(estado)}`;
    }
    
    const response = await apiFetch(endpoint);
    
    let productos = [];
    
    if (response.success && Array.isArray(response.data)) {
      productos = response.data;
    } else if (Array.isArray(response)) {
      productos = response;
    } else if (response.data && Array.isArray(response.data)) {
      productos = response.data;
    }
    
    return {
      success: true,
      data: productos,
      total: productos.length,
      count: productos.length,
      message: 'Productos de recepción obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo productos de recepción:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      total: 0,
      count: 0
    };
  }
};

/**
 * Obtener productos de recepción disponibles
 * @returns {Promise} Productos disponibles
 */
export const obtenerProductosRecepcionDisponibles = async () => {
  try {
    console.log('📋 Obteniendo productos de recepción disponibles...');
    
    const response = await apiFetch(`${API_BASE}/disponibles`);
    
    let productos = [];
    
    if (response.success && Array.isArray(response.data)) {
      productos = response.data;
    } else if (Array.isArray(response)) {
      productos = response;
    } else if (response.data && Array.isArray(response.data)) {
      productos = response.data;
    }
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: 'Productos disponibles obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo productos disponibles:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener productos de recepción por estado
 * @param {string} estado - Estado del producto (disponible, vendido, reservado, etc.)
 * @returns {Promise} Productos con el estado especificado
 */
export const obtenerProductosRecepcionPorEstado = async (estado) => {
  try {
    if (!estado) throw new Error('Estado es requerido');
    
    console.log(`📋 Obteniendo productos de recepción con estado: ${estado}`);
    
    const response = await apiFetch(`${API_BASE}/estado/${encodeURIComponent(estado)}`);
    
    let productos = [];
    
    if (response.success && Array.isArray(response.data)) {
      productos = response.data;
    } else if (Array.isArray(response)) {
      productos = response;
    } else if (response.data && Array.isArray(response.data)) {
      productos = response.data;
    }
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: `Productos con estado "${estado}" obtenidos`
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo productos por estado ${estado}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener un producto de recepción por ID
 * @param {number} id - ID del producto de recepción
 * @returns {Promise} Datos del producto
 */
export const obtenerProductoRecepcionPorId = async (id) => {
  try {
    if (!id) throw new Error('ID de producto de recepción es requerido');
    
    console.log(`🔍 Obteniendo producto de recepción ID: ${id}`);
    
    const response = await apiFetch(`${API_BASE}/${id}`);
    
    const producto = response.success ? response.data : response;
    
    return {
      success: true,
      data: producto,
      message: response.message || 'Producto de recepción obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo producto de recepción ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener producto de recepción con relaciones (categoría, etc.)
 * @param {number} id - ID del producto de recepción
 * @returns {Promise} Datos del producto con relaciones
 */
export const obtenerProductoRecepcionConRelaciones = async (id) => {
  try {
    if (!id) throw new Error('ID de producto de recepción es requerido');
    
    console.log(`🔍 Obteniendo producto de recepción ID: ${id} con relaciones`);
    
    const response = await apiFetch(`${API_BASE}/${id}/conRelaciones`);
    
    const producto = response.success ? response.data : response;
    
    return {
      success: true,
      data: producto,
      message: response.message || 'Producto de recepción obtenido con relaciones'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo producto de recepción con relaciones ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener producto de recepción por código
 * @param {string} codigo - Código del producto
 * @returns {Promise} Datos del producto
 */
export const obtenerProductoRecepcionPorCodigo = async (codigo) => {
  try {
    if (!codigo) throw new Error('Código de producto es requerido');
    
    console.log(`🔍 Obteniendo producto de recepción por código: ${codigo}`);
    
    const response = await apiFetch(`${API_BASE}/codigo/${encodeURIComponent(codigo)}`);
    
    const producto = response.success ? response.data : response;
    
    return {
      success: true,
      data: producto,
      message: response.message || 'Producto de recepción obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo producto de recepción por código ${codigo}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Crear un nuevo producto de recepción
 * @param {Object} productoData - Datos del producto (en snake_case)
 * @returns {Promise} Producto creado
 */
export const crearProductoRecepcion = async (productoData) => {
  try {
    // Validaciones
    if (!productoData.codigo?.trim()) throw new Error('El código es requerido');
    if (!productoData.nombre?.trim()) throw new Error('El nombre es requerido');
    if (!productoData.idCategoria) throw new Error('La categoría es requerida');
    if (!productoData.precio_estimado_usd || productoData.precio_estimado_usd <= 0) {
      throw new Error('El precio estimado debe ser mayor a 0');
    }
    
    console.log('➕ Creando nuevo producto de recepción:', productoData);
    
    // Preparar datos en el formato que espera la API
    const datosEnviar = {
      codigo: productoData.codigo.trim(),
      nombre: productoData.nombre.trim(),
      descripcion: productoData.descripcion?.trim() || null,
      marca: productoData.marca?.trim() || null,
      modelo: productoData.modelo?.trim() || null,
      precio_estimado_usd: Number(productoData.precio_estimado_usd),
      estado: productoData.estado || 'disponible',
      idCategoria: Number(productoData.idCategoria)
    };
    
    const response = await apiFetch(API_BASE, {
      method: 'POST',
      body: JSON.stringify(datosEnviar)
    });
    
    if (!response.success) {
      throw new Error(response.message || 'Error al crear producto de recepción');
    }
    
    return {
      success: true,
      data: response.data,
      message: response.message || 'Producto de recepción creado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando producto de recepción:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Actualizar un producto de recepción existente
 * @param {number} id - ID del producto de recepción
 * @param {Object} productoData - Datos a actualizar
 * @returns {Promise} Producto actualizado
 */
export const actualizarProductoRecepcion = async (id, productoData) => {
  try {
    if (!id) throw new Error('ID de producto de recepción es requerido');
    
    console.log(`✏️ Actualizando producto de recepción ID: ${id}`, productoData);
    
    // Preparar datos en el formato que espera la API
    const datosEnviar = {
      codigo: productoData.codigo?.trim(),
      nombre: productoData.nombre?.trim(),
      descripcion: productoData.descripcion?.trim(),
      marca: productoData.marca?.trim(),
      modelo: productoData.modelo?.trim(),
      precio_estimado_usd: productoData.precio_estimado_usd !== undefined 
        ? Number(productoData.precio_estimado_usd) 
        : undefined,
      estado: productoData.estado,
      idCategoria: productoData.idCategoria !== undefined 
        ? Number(productoData.idCategoria) 
        : undefined
    };
    
    // Eliminar undefined
    Object.keys(datosEnviar).forEach(key => 
      datosEnviar[key] === undefined && delete datosEnviar[key]
    );
    
    const response = await apiFetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(datosEnviar)
    });
    
    if (!response.success) {
      throw new Error(response.message || 'Error al actualizar producto de recepción');
    }
    
    return {
      success: true,
      data: response.data,
      message: response.message || 'Producto de recepción actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando producto de recepción ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Eliminar un producto de recepción
 * @param {number} id - ID del producto a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarProductoRecepcion = async (id) => {
  try {
    if (!id) throw new Error('ID de producto de recepción es requerido');
    
    console.log(`🗑️ Eliminando producto de recepción ID: ${id}`);
    
    const response = await apiFetch(`${API_BASE}/${id}`, { 
      method: 'DELETE' 
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Producto de recepción eliminado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando producto de recepción ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Cambiar estado de un producto de recepción
 * @param {number} id - ID del producto
 * @param {string} estado - Nuevo estado (disponible, vendido, reservado, etc.)
 * @returns {Promise} Resultado del cambio de estado
 */
export const cambiarEstadoProductoRecepcion = async (id, estado) => {
  try {
    if (!id) throw new Error('ID es requerido');
    if (!estado) throw new Error('Estado es requerido');
    
    console.log(`🔄 Cambiando estado de producto de recepción ID: ${id} a ${estado}`);
    
    return await actualizarProductoRecepcion(id, { estado });
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de producto de recepción ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

// ==================== CONSULTAS ESPECÍFICAS ====================

/**
 * Obtener productos de recepción por categoría
 * @param {number} idCategoria - ID de la categoría
 * @returns {Promise} Productos de la categoría
 */
export const obtenerProductosRecepcionPorCategoria = async (idCategoria) => {
  try {
    if (!idCategoria) throw new Error('ID de categoría es requerido');
    
    console.log(`📂 Obteniendo productos de recepción de categoría ID: ${idCategoria}`);
    
    const response = await apiFetch(`${API_BASE}/categoria/${idCategoria}`);
    
    const productos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: 'Productos de recepción por categoría obtenidos'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo productos de recepción por categoría ${idCategoria}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener productos de recepción por marca
 * @param {string} marca - Marca del producto
 * @returns {Promise} Productos de la marca
 */
export const obtenerProductosRecepcionPorMarca = async (marca) => {
  try {
    if (!marca?.trim()) throw new Error('Marca es requerida');
    
    console.log(`🏷️ Obteniendo productos de recepción por marca: ${marca}`);
    
    const response = await apiFetch(`${API_BASE}/marca/${encodeURIComponent(marca)}`);
    
    const productos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: 'Productos de recepción por marca obtenidos'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo productos de recepción por marca ${marca}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener productos de recepción por modelo
 * @param {string} modelo - Modelo del producto
 * @returns {Promise} Productos con el modelo
 */
export const obtenerProductosRecepcionPorModelo = async (modelo) => {
  try {
    if (!modelo?.trim()) throw new Error('Modelo es requerido');
    
    console.log(`🔧 Obteniendo productos de recepción por modelo: ${modelo}`);
    
    const response = await apiFetch(`${API_BASE}/modelo/${encodeURIComponent(modelo)}`);
    
    const productos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: 'Productos de recepción por modelo obtenidos'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo productos de recepción por modelo ${modelo}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Obtener productos de recepción por rango de precio
 * @param {number} min - Precio mínimo
 * @param {number} max - Precio máximo
 * @returns {Promise} Productos en el rango de precio
 */
export const obtenerProductosRecepcionPorRangoPrecio = async (min, max) => {
  try {
    if (!min || min <= 0) throw new Error('Precio mínimo válido es requerido');
    if (!max || max <= 0) throw new Error('Precio máximo válido es requerido');
    if (min > max) throw new Error('El precio mínimo no puede ser mayor al máximo');
    
    console.log(`💰 Obteniendo productos de recepción en rango: $${min} - $${max}`);
    
    const response = await apiFetch(`${API_BASE}/precio/${min}/${max}`);
    
    const productos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: 'Productos de recepción por rango de precio obtenidos'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo productos por rango de precio:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

/**
 * Buscar productos de recepción avanzado
 * @param {Object} criterios - Criterios de búsqueda
 * @returns {Promise} Productos encontrados
 */
export const buscarProductosRecepcionAvanzado = async (criterios = {}) => {
  try {
    console.log('🔎 Buscando productos de recepción con criterios:', criterios);
    
    const response = await apiFetch(`${API_BASE}/search`, {
      method: 'POST',
      body: JSON.stringify(criterios)
    });
    
    const productos = response.success ? response.data : (Array.isArray(response) ? response : []);
    
    return {
      success: true,
      data: productos,
      count: productos.length,
      message: response.message || 'Búsqueda completada'
    };
    
  } catch (error) {
    console.error('❌ Error en búsqueda avanzada:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

// ==================== VALIDACIONES ====================

/**
 * Verificar si un producto de recepción existe
 * @param {number} id - ID del producto
 * @returns {Promise} Resultado de verificación
 */
export const verificarProductoRecepcionExiste = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    
    console.log(`🔍 Verificando existencia de producto de recepción ID: ${id}`);
    
    const response = await apiFetch(`${API_BASE}/${id}/exists`);
    
    return {
      success: true,
      existe: response?.existe || false,
      data: response,
      message: response?.message || 'Verificación completada'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando existencia de producto ${id}:`, error);
    return { success: false, error: error.message, existe: false };
  }
};

/**
 * Verificar si un código existe en productos de recepción
 * @param {string} codigo - Código a verificar
 * @returns {Promise} Resultado de verificación
 */
export const verificarCodigoProductoRecepcion = async (codigo) => {
  try {
    if (!codigo?.trim()) throw new Error('Código es requerido');
    
    console.log(`🔍 Verificando código en productos de recepción: ${codigo}`);
    
    const response = await apiFetch(`${API_BASE}/codigo/${encodeURIComponent(codigo)}/exists`);
    
    return {
      success: true,
      existe: response?.existe || false,
      data: response,
      message: response?.message || 'Verificación completada'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando código ${codigo}:`, error);
    return { success: false, error: error.message, existe: false };
  }
};

/**
 * Verificar si un nombre existe en productos de recepción
 * @param {string} nombre - Nombre a verificar
 * @returns {Promise} Resultado de verificación
 */
export const verificarNombreProductoRecepcion = async (nombre) => {
  try {
    if (!nombre?.trim()) throw new Error('Nombre es requerido');
    
    console.log(`🔍 Verificando nombre en productos de recepción: ${nombre}`);
    
    const response = await apiFetch(`${API_BASE}/nombre/${encodeURIComponent(nombre)}/exists`);
    
    return {
      success: true,
      existe: response?.existe || false,
      data: response,
      message: response?.message || 'Verificación completada'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando nombre ${nombre}:`, error);
    return { success: false, error: error.message, existe: false };
  }
};

// ==================== ESTADÍSTICAS Y DASHBOARD ====================

/**
 * Obtener estadísticas de productos de recepción
 * @returns {Promise} Estadísticas
 */
export const obtenerEstadisticasProductosRecepcion = async () => {
  try {
    console.log('📊 Obteniendo estadísticas de productos de recepción');
    
    // Obtener todos los productos y calcular estadísticas localmente
    const productosRes = await obtenerProductosRecepcion('todos');
    
    if (!productosRes.success) {
      throw new Error(productosRes.error);
    }
    
    const productos = productosRes.data;
    
    const disponibles = productos.filter(p => p.estado?.toLowerCase() === 'disponible').length;
    const vendidos = productos.filter(p => p.estado?.toLowerCase() === 'vendido').length;
    const reservados = productos.filter(p => p.estado?.toLowerCase() === 'reservado').length;
    
    const valorTotalEstimado = productos.reduce((sum, p) => sum + (Number(p.precio_estimado_usd) || 0), 0);
    const valorDisponible = productos
      .filter(p => p.estado?.toLowerCase() === 'disponible')
      .reduce((sum, p) => sum + (Number(p.precio_estimado_usd) || 0), 0);
    
    const precioPromedio = productos.length > 0 
      ? valorTotalEstimado / productos.length 
      : 0;
    
    return {
      success: true,
      data: {
        total: productos.length,
        disponibles,
        vendidos,
        reservados,
        otros: productos.length - (disponibles + vendidos + reservados),
        porcentajeDisponibles: productos.length > 0 ? (disponibles / productos.length) * 100 : 0,
        porcentajeVendidos: productos.length > 0 ? (vendidos / productos.length) * 100 : 0,
        valorTotalEstimado: Number(valorTotalEstimado.toFixed(2)),
        valorDisponible: Number(valorDisponible.toFixed(2)),
        precioPromedio: Number(precioPromedio.toFixed(2))
      },
      message: 'Estadísticas obtenidas'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo estadísticas:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Obtener dashboard de productos de recepción
 * @returns {Promise} Datos del dashboard
 */
export const obtenerDashboardProductosRecepcion = async () => {
  try {
    console.log('📊 Obteniendo dashboard de productos de recepción');
    
    const [todos, disponibles, estadisticas] = await Promise.all([
      obtenerProductosRecepcion('todos'),
      obtenerProductosRecepcionDisponibles(),
      obtenerEstadisticasProductosRecepcion()
    ]);
    
    return {
      success: true,
      data: {
        total: todos.data.length,
        disponibles: disponibles.data.length,
        recientes: todos.data.slice(0, 10),
        estadisticas: estadisticas.data,
        ultimaActualizacion: new Date().toISOString()
      },
      message: 'Dashboard obtenido'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo dashboard:', error);
    return { success: false, error: error.message, data: null };
  }
};

// ==================== FUNCIONES AUXILIARES ====================

/**
 * Formatear número con formato venezolano
 * @param {number|string} numero - Número a formatear
 * @param {number} decimales - Número de decimales
 * @returns {string} Número formateado
 */
export const formatearMonto = (numero, decimales = 2) => {
  if (numero === null || numero === undefined || numero === '' || isNaN(numero)) {
    return decimales === 2 ? '0,00' : '0';
  }
  
  const num = Number(numero);
  if (isNaN(num)) return decimales === 2 ? '0,00' : '0';
  
  return num.toLocaleString('es-VE', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales
  });
};

/**
 * Formatear fecha
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora
 * @returns {string} Fecha formateada
 */
export const formatearFecha = (fechaISO, completo = true) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'America/Caracas'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
      opciones.hour12 = false;
    }
    
    return fecha.toLocaleDateString('es-VE', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Convertir estado a texto legible
 * @param {string} estado - Estado del producto
 * @returns {string} Estado formateado
 */
export const convertirEstadoProductoRecepcionATexto = (estado) => {
  if (!estado) return 'Sin estado';
  const estadoLower = estado.toLowerCase();
  if (estadoLower === 'disponible') return 'Disponible';
  if (estadoLower === 'vendido') return 'Vendido';
  if (estadoLower === 'reservado') return 'Reservado';
  return estado;
};

/**
 * Obtener clase CSS para estado
 * @param {string} estado - Estado del producto
 * @returns {string} Clase CSS
 */
export const obtenerClaseEstadoRecepcion = (estado) => {
  const estadoLower = estado?.toLowerCase();
  switch (estadoLower) {
    case 'disponible':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'vendido':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'reservado':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
};

/**
 * Buscar productos de recepción localmente
 * @param {Array} productos - Lista de productos
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Productos filtrados
 */
export const buscarProductosRecepcionLocal = (productos, termino) => {
  if (!termino?.trim() || !Array.isArray(productos)) return productos || [];
  
  const terminoLower = termino.toLowerCase().trim();
  return productos.filter(producto => 
    (producto.codigo?.toLowerCase().includes(terminoLower)) ||
    (producto.nombre?.toLowerCase().includes(terminoLower)) ||
    (producto.descripcion?.toLowerCase().includes(terminoLower)) ||
    (producto.marca?.toLowerCase().includes(terminoLower)) ||
    (producto.modelo?.toLowerCase().includes(terminoLower)) ||
    (producto.nombreCategoria?.toLowerCase().includes(terminoLower))
  );
};

/**
 * Filtrar productos de recepción por estado
 * @param {Array} productos - Lista de productos
 * @param {string} estado - Estado a filtrar
 * @returns {Array} Productos filtrados
 */
export const filtrarProductosRecepcionPorEstado = (productos, estado) => {
  if (!Array.isArray(productos)) return [];
  if (!estado || estado === 'todos') return productos;
  
  const estadoLower = estado.toLowerCase();
  return productos.filter(p => p.estado?.toLowerCase() === estadoLower);
};

/**
 * Ordenar productos de recepción por diferentes criterios
 * @param {Array} productos - Lista de productos
 * @param {string} criterio - Criterio de ordenamiento
 * @returns {Array} Productos ordenados
 */
export const ordenarProductosRecepcion = (productos, criterio = 'nombre') => {
  const array = [...(Array.isArray(productos) ? productos : [])];
  
  const criterios = {
    nombre: (a, b) => (a.nombre || '').localeCompare(b.nombre || ''),
    nombre_desc: (a, b) => (b.nombre || '').localeCompare(a.nombre || ''),
    codigo: (a, b) => (a.codigo || '').localeCompare(b.codigo || ''),
    codigo_desc: (a, b) => (b.codigo || '').localeCompare(a.codigo || ''),
    precio_desc: (a, b) => Number(b.precio_estimado_usd || 0) - Number(a.precio_estimado_usd || 0),
    precio: (a, b) => Number(a.precio_estimado_usd || 0) - Number(b.precio_estimado_usd || 0),
    fechaCreacion_desc: (a, b) => new Date(b.fecha_creacion || 0) - new Date(a.fecha_creacion || 0),
    fechaCreacion: (a, b) => new Date(a.fecha_creacion || 0) - new Date(b.fecha_creacion || 0)
  };
  
  return array.sort(criterios[criterio] || criterios.nombre);
};

/**
 * Obtener estadísticas de productos de recepción localmente
 * @param {Array} productos - Lista de productos
 * @returns {Object} Estadísticas
 */
export const obtenerEstadisticasProductosRecepcionLocal = (productos) => {
  const array = Array.isArray(productos) ? productos : [];
  
  const disponibles = array.filter(p => p?.estado?.toLowerCase() === 'disponible').length;
  const vendidos = array.filter(p => p?.estado?.toLowerCase() === 'vendido').length;
  const reservados = array.filter(p => p?.estado?.toLowerCase() === 'reservado').length;
  
  const valorTotalEstimado = array.reduce((sum, p) => sum + (Number(p.precio_estimado_usd) || 0), 0);
  const valorDisponible = array
    .filter(p => p?.estado?.toLowerCase() === 'disponible')
    .reduce((sum, p) => sum + (Number(p.precio_estimado_usd) || 0), 0);
  
  const precioPromedio = array.length > 0 ? valorTotalEstimado / array.length : 0;
  const precioPromedioDisponible = disponibles > 0 ? valorDisponible / disponibles : 0;
  
  return {
    total: array.length,
    disponibles,
    vendidos,
    reservados,
    otros: array.length - (disponibles + vendidos + reservados),
    porcentajeDisponibles: array.length > 0 ? (disponibles / array.length) * 100 : 0,
    porcentajeVendidos: array.length > 0 ? (vendidos / array.length) * 100 : 0,
    valorTotalEstimado: Number(valorTotalEstimado.toFixed(2)),
    valorDisponible: Number(valorDisponible.toFixed(2)),
    precioPromedio: Number(precioPromedio.toFixed(2)),
    precioPromedioDisponible: Number(precioPromedioDisponible.toFixed(2))
  };
};

/**
 * Función auxiliar para mostrar mensajes de error
 * @param {Error} error - Error capturado
 * @param {string} operacion - Operación que falló
 * @returns {string} Mensaje de error
 */
export const obtenerMensajeError = (error, operacion = 'operación') => {
  const mensaje = error.message || 'Error desconocido';
  
  if (mensaje.includes('Ya existe un producto con el código')) {
    return 'El código del producto ya existe. Por favor, utiliza un código diferente.';
  }
  
  if (mensaje.includes('Failed to fetch') || mensaje.includes('Network Error')) {
    return 'Error de conexión con el servidor. Verifica tu conexión a internet.';
  }
  
  if (mensaje.includes('401') || mensaje.includes('Unauthorized')) {
    return 'No autorizado. Por favor, inicia sesión nuevamente.';
  }
  
  return `Error al ${operacion}: ${mensaje}`;
};

// ==================== FUNCIONES DE EXPORTACIÓN ====================

/**
 * Exportar productos de recepción a Excel
 * @param {Array} productos - Lista de productos
 * @param {string} filename - Nombre del archivo
 * @returns {Object} Resultado de la exportación
 */
export const exportarExcel = (productos, filename = 'productos_recepcion') => {
  try {
    if (typeof window === 'undefined') {
      return { success: false, error: 'Exportación solo disponible en el navegador', filename: null };
    }
    
    if (!productos?.length) throw new Error('No hay productos para exportar');
    
    const XLSX = require('xlsx');
    
    const data = productos.map((producto, index) => ({
      '#': index + 1,
      'Código': producto.codigo || '',
      'Nombre': producto.nombre || '',
      'Marca': producto.marca || '',
      'Modelo': producto.modelo || '',
      'Categoría': producto.nombreCategoria || '',
      'Precio Estimado ($)': formatearMonto(producto.precio_estimado_usd, 2),
      'Estado': convertirEstadoProductoRecepcionATexto(producto.estado),
      'Fecha Creación': formatearFecha(producto.fecha_creacion, true)
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Productos Recepción');
    
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `${filename}_${fecha}.xlsx`);
    
    return {
      success: true,
      filename: `${filename}_${fecha}.xlsx`,
      message: `Archivo generado exitosamente`,
      count: productos.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a Excel:', error);
    return { success: false, error: error.message, filename: null };
  }
};

/**
 * Exportar a PDF
 * @param {Array} productos - Lista de productos
 * @param {string} titulo - Título del reporte
 * @returns {Object} Resultado de la exportación
 */
export const exportarPDF = async (productos, titulo = 'Reporte de Productos en Recepción') => {
  try {
    if (typeof window === 'undefined') {
      return { success: false, error: 'Exportación solo disponible en el navegador', filename: null };
    }
    
    if (!productos?.length) throw new Error('No hay productos para exportar');
    
    const { jsPDF } = await import('jspdf');
    await import('jspdf-autotable');
    
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.width;
    
    doc.setFontSize(16);
    doc.text('JEAN INFORMATICO C.A.', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(12);
    doc.text(titulo, pageWidth / 2, 22, { align: 'center' });
    
    const tableData = productos.map(producto => [
      producto.codigo || '',
      producto.nombre?.substring(0, 20) || '',
      producto.marca || '',
      producto.modelo || '',
      formatearMonto(producto.precio_estimado_usd, 2),
      convertirEstadoProductoRecepcionATexto(producto.estado)
    ]);
    
    doc.autoTable({
      startY: 30,
      head: [['Código', 'Nombre', 'Marca', 'Modelo', 'Precio Est. ($)', 'Estado']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
      styles: { fontSize: 8 }
    });
    
    const fecha = new Date().toISOString().split('T')[0];
    doc.save(`productos_recepcion_${fecha}.pdf`);
    
    return {
      success: true,
      filename: `productos_recepcion_${fecha}.pdf`,
      message: 'PDF generado exitosamente',
      count: productos.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando PDF:', error);
    return { success: false, error: error.message, filename: null };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Funciones API principales
  obtenerProductosRecepcion,
  obtenerProductosRecepcionDisponibles,
  obtenerProductosRecepcionPorEstado,
  obtenerProductoRecepcionPorId,
  obtenerProductoRecepcionConRelaciones,
  obtenerProductoRecepcionPorCodigo,
  crearProductoRecepcion,
  actualizarProductoRecepcion,
  eliminarProductoRecepcion,
  cambiarEstadoProductoRecepcion,
  
  // Consultas específicas
  obtenerProductosRecepcionPorCategoria,
  obtenerProductosRecepcionPorMarca,
  obtenerProductosRecepcionPorModelo,
  obtenerProductosRecepcionPorRangoPrecio,
  buscarProductosRecepcionAvanzado,
  
  // Validaciones
  verificarProductoRecepcionExiste,
  verificarCodigoProductoRecepcion,
  verificarNombreProductoRecepcion,
  
  // Estadísticas y dashboard
  obtenerEstadisticasProductosRecepcion,
  obtenerDashboardProductosRecepcion,
  
  // Funciones auxiliares
  formatearMonto,
  formatearFecha,
  convertirEstadoProductoRecepcionATexto,
  obtenerClaseEstadoRecepcion,
  
  // Funciones de búsqueda y filtrado
  buscarProductosRecepcionLocal,
  filtrarProductosRecepcionPorEstado,
  ordenarProductosRecepcion,
  obtenerEstadisticasProductosRecepcionLocal,
  
  // Utilidades
  obtenerMensajeError,
  
  // Exportación
  exportarExcel,
  exportarPDF
};